import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AutocompleteCDS, {
  onAutocompleteChangeInForm,
} from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import {
  getHalcyonSpecificYearOptions,
  halcyonFormGroupBy,
  HalcyonMultiYearEnum,
  halcyonYearModeOptions,
} from 'constants/loanActionEditor';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';

import {
  loadHalcyonFormCatalogAction,
  selectHalcyonFormPickerOptions,
} from 'store/loanActions/halcyonFormCatalog';

import { HalcyonAddFormsFormTestIds } from './HalcyonAddFormsForm.testIds';
import { getHalcyonAddFormsSchema } from './HalcyonAddFormsForm.utils';

const HalcyonAddFormsForm = ({ isLoading, setIsLoading, formRef, onSubmit }) => {
  const {
    form,
    setSchema,
    setInForm,
    isFormChanged,
    validateForm,
    validateField,
    validationErrors,
  } = useForm({});

  const dispatch = useDispatch();
  const halcyonFormOptions = useSelector(selectHalcyonFormPickerOptions);

  const specificYearOptions = useMemo(() => getHalcyonSpecificYearOptions(), []);

  const isSpecificYears = form.yearMode?.value === HalcyonMultiYearEnum.SPECIFIC;

  useEffect(() => {
    setSchema(getHalcyonAddFormsSchema({ isSpecificYears }));
  }, [setSchema, isSpecificYears]);

  useEffectSafeStrictMode(() => {
    dispatch(loadHalcyonFormCatalogAction());
  }, [dispatch]);

  const formsValue = form.forms || [];
  const specificYearsValue = form.specificYears || [];

  const onSubmitForm = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setIsLoading(true);

      onSubmit({
        forms: (form.forms || []).map(f => ({
          formType: f.value,
          yearMode: form.yearMode?.value,
          ...(isSpecificYears
            ? { specificYears: (form.specificYears || []).map(y => y.value) }
            : {}),
        })),
      }).finally(() => setIsLoading(false));
    },
    [validateForm, form, onSubmit, isSpecificYears, setIsLoading],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <ModalFormWrapper
      formRef={formRef}
      formTestId="halcyon-add-forms-form"
      isFormChanged={isFormChanged}
      isSubmitting={isLoading}
      submitButtonName="Submit"
      onSubmit={onSubmitForm}
    >
      <AutocompleteCDS
        disableClearable
        multiple
        required
        containerClassName="mb-24"
        dataTestId={HalcyonAddFormsFormTestIds.FORMS_SELECT}
        disabled={isLoading}
        error={!!validationErrors.forms}
        groupBy={halcyonFormGroupBy}
        helperText={validationErrors.forms}
        label="Forms"
        name="forms"
        options={halcyonFormOptions}
        value={formsValue}
        onChange={onAutocompleteChange}
      />
      <AutocompleteCDS
        disableClearable
        required
        containerClassName="mb-24"
        dataTestId={HalcyonAddFormsFormTestIds.YEAR_MODE_SELECT}
        disabled={isLoading}
        error={!!validationErrors.yearMode}
        helperText={validationErrors.yearMode}
        label="Year Mode"
        name="yearMode"
        options={halcyonYearModeOptions}
        value={form.yearMode || ''}
        onChange={onAutocompleteChange}
      />
      {isSpecificYears && (
        <AutocompleteCDS
          disableClearable
          multiple
          required
          containerClassName="mb-24"
          dataTestId={HalcyonAddFormsFormTestIds.SPECIFIC_YEARS_SELECT}
          disabled={isLoading}
          error={!!validationErrors.specificYears}
          helperText={validationErrors.specificYears}
          label="Specific Years"
          name="specificYears"
          options={specificYearOptions}
          value={specificYearsValue}
          onChange={onAutocompleteChange}
        />
      )}
    </ModalFormWrapper>
  );
};

HalcyonAddFormsForm.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(HalcyonAddFormsForm);
